/** The running build's own `/version.json`, written by `frontend/Dockerfile`
 *  at image build time and served from the web root by nginx.
 *
 *  `release` is the human-readable name (`0.5.7`), `version` the 8-char commit
 *  SHA that is also the image tag, `commit` the full SHA. All three fall back
 *  to their Dockerfile defaults (`dev` / `dev` / `unknown`) on a build that did
 *  not go through the deploy workflow. */
export type BuildVersion = {
  release: string;
  version: string;
  commit: string;
};

function isBuildVersion(value: unknown): value is BuildVersion {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.release === 'string' &&
    typeof v.version === 'string' &&
    typeof v.commit === 'string'
  );
}

export const versionService = {
  /** Deliberately a bare `fetch` rather than `apiClient`: this is a static
   *  same-origin file, not an API call. Going through the axios instance would
   *  attach the user's Bearer token to it and expose it to the 401 interceptor,
   *  neither of which a build manifest should be involved in.
   *
   *  Resolves to `null` rather than throwing whenever the payload cannot be
   *  trusted. A 200 is not enough on its own: under `vite dev` there is no
   *  `/version.json`, and the SPA fallback answers with `index.html`. */
  buildVersion: async (): Promise<BuildVersion | null> => {
    let payload: unknown;
    try {
      const res = await fetch('/version.json', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      payload = await res.json();
    } catch {
      return null;
    }
    return isBuildVersion(payload) ? payload : null;
  },
};
